import { publish } from '~/event-bus';
import { classes, getCartSectionNames, getTargets, parseHTML } from '~/helpers';
import { HTMLCustomElement } from '~/html-custom-element';

if (!customElements.get('cart-drawer'))
  customElements.define(
    'cart-drawer',
    class CartDrawer extends HTMLCustomElement {
      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();

        this.subscribe('cart:added', this.createRecommendations.bind(this));
      }

      /**
       * Creates the cart recommendations section.
       * @param {Object} payload - The cart added payload.
       * @param {String} payload.productId - The product to recommend for.
       */
      createRecommendations({ productId } = {}) {
        const { recommendations } = getTargets(this);

        if (!productId) {
          return;
        }

        fetch(`${theme.routes.product_recommendations_url}?product_id=${productId}&section_id=cart-drawer`)
          .then((response) => response.text())
          .then((response) => {
            const element = parseHTML(response, 'cart-drawer');
            const targets = getTargets(element);

            recommendations.innerHTML = targets.recommendations.innerHTML;

            requestAnimationFrame(() => {
              setTimeout(() => {
                recommendations.firstElementChild.classList.add(classes.active);
              });
            });
          });
      }
    }
  );

if (!customElements.get('cart-note-drawer'))
  customElements.define(
    'cart-note-drawer',
    class CartNoteDrawer extends HTMLCustomElement {
      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();

        this.note = this.querySelector('[name=note]');

        this.on('click', this.targets.update, this.handleUpdateEvent.bind(this));
        this.on('click', this.targets.remove, this.handleRemoveEvent.bind(this));
        this.on('input', this.note, this.handleKeyup.bind(this));
      }

      /**
       * Handles the update note button event.
       */
      handleUpdateEvent() {
        this.targets.update.disabled = true;
        this.targets.update.innerHTML = theme.strings.sections.cart_drawer.note.updating;

        this.update(this.note.value).then(() => {
          this.targets.update.disabled = false;
          this.targets.update.innerHTML = theme.strings.sections.cart_drawer.note.update;
        });
      }

      /**
       * Handles the remove note button event.
       */
      handleRemoveEvent() {
        this.targets.remove.disabled = true;
        this.targets.remove.innerHTML = theme.strings.sections.cart_drawer.note.removing;

        this.update().then(() => {
          this.targets.remove.disabled = false;
          this.targets.remove.innerHTML = theme.strings.sections.cart_drawer.note.remove;
        });
      }

      /**
       * Updates the cart note.
       * @param {String} note - The note to apply, omit to remote.
       * @returns {Promise}
       */
      update(note = '') {
        return fetch(`${theme.routes.cart_update_url}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            note,
            sections: getCartSectionNames().join(','),
          }),
        })
          .then((response) => response.json())
          .then((response) => {
            publish('cart:update', { sections: response.sections });
            publish('toggle-element-close', { id: 'cart-note-drawer' });
          });
      }

      /**
       * Handles the keyup event on the note input.
       * @param {KeyboardEvent} event - The event payload.
       */
      handleKeyup(event) {
        this.targets.characters.textContent = event.currentTarget.value.length;
      }
    }
  );

if (!customElements.get('gift-wrap-drawer'))
  customElements.define(
    'gift-wrap-drawer',
    class GiftWrapDrawer extends HTMLCustomElement {
      connectedCallback() {
        if (super.connectedCallback) super.connectedCallback();

        this.on('submit', this.targets.form, (event) => {
          const body = new FormData(event.target);

          event.preventDefault();

          body.append('sections', getCartSectionNames());

          this.targets.submit.disabled = true;
          this.targets.submit.innerHTML = theme.strings.sections.cart_drawer.gift_wrap.adding;

          fetch(theme.routes.cart_add_url, {
            method: 'POST',
            headers: {
              Accept: 'application/javascript',
            },
            body,
          })
            .then((response) => response.json())
            .then((response) => {
              publish('cart:update', { sections: response.sections });
              publish('cart:added');
              publish('toggle-element-close', { id: 'gift-wrap-drawer' });

              this.targets.submit.disabled = false;
              this.targets.submit.innerHTML = theme.strings.sections.cart_drawer.gift_wrap.add;
            })
            .catch(() => {
              event.target.submit();
            });
        });
      }
    }
  );
